import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { EmptyState, ErrorNotice, Field, Spinner, formatDate } from '../components/ui';
import type { Newsletter, NewsletterSummary } from '../types';

/**
 * Every newsletter, newest first, and the button that starts a new one.
 *
 * Starting one asks for two things and no more. A volunteer with something to
 * say should be writing it within a few seconds of deciding to; the rest of
 * the decisions belong on the composer, where the newsletter is in front of
 * them.
 */
export function NewslettersPage() {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState<NewsletterSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [starting, setStarting] = useState(false);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ newsletters: NewsletterSummary[] }>('/newsletters')
      .then((r) => setNewsletters(r.newsletters))
      .catch(setError);
  }, []);

  async function start(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ newsletter: Newsletter }>('/newsletters', {
        title,
        subject,
      });
      navigate(`/email/newsletters/${result.newsletter.id}`);
    } catch (failure) {
      setError(failure);
      setBusy(false);
    }
  }

  return (
    <>
      <p className="muted small">
        <Link to="/email">← Back to email</Link>
      </p>

      <div className="page-head">
        <h1>Newsletters</h1>
        {starting ? null : (
          <button
            type="button"
            className="primary"
            onClick={() => {
              // Pre-filled with the month, because that is what it nearly
              // always is, and a filled field is quicker to correct than an
              // empty one is to fill.
              const month = new Date().toLocaleDateString('en-GB', {
                month: 'long',
                year: 'numeric',
              });
              setTitle(month);
              setSubject(`${month} newsletter`);
              setStarting(true);
            }}
          >
            Write a new one
          </button>
        )}
      </div>

      <ErrorNotice error={error} />

      {starting ? (
        <form className="card form wide" onSubmit={start}>
          <h2>A new newsletter</h2>
          <Field
            label="A name for it"
            hint="Only the committee sees this. It is how you will find it again."
            value={title}
            required
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
          />
          <Field
            label="The subject line members will see"
            hint="You can change this at any point before it goes out."
            value={subject}
            required
            onChange={(e) => setSubject(e.target.value)}
          />
          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Starting…' : 'Start writing'}
            </button>
            <button type="button" onClick={() => setStarting(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {newsletters === null ? (
        <Spinner label="Loading newsletters" />
      ) : newsletters.length === 0 ? (
        <EmptyState title="No newsletters yet">
          A newsletter is built from blocks — headings, paragraphs, pictures, a list of dates — and
          you can see exactly how it will look while you write it.
        </EmptyState>
      ) : (
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Subject</th>
                <th scope="col">State</th>
                <th scope="col">Last changed</th>
                <th scope="col">Started by</th>
              </tr>
            </thead>
            <tbody>
              {newsletters.map((n) => (
                <tr key={n.id}>
                  <td>
                    <Link to={`/email/newsletters/${n.id}`}>{n.title}</Link>
                    <br />
                    <span className="hint">
                      {n.block_count} {n.block_count === 1 ? 'block' : 'blocks'}
                    </span>
                  </td>
                  <td>{n.subject}</td>
                  <td>
                    <span className={`pill pill-newsletter-${n.status}`}>
                      {n.status === 'sent' ? 'Sent' : 'Draft'}
                    </span>
                    {n.status === 'sent' ? (
                      <>
                        <br />
                        <span className="hint">
                          {formatDate(n.sent_at)} · {n.sent_count}{' '}
                          {n.sent_count === 1 ? 'recipient' : 'recipients'}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>{formatDate(n.updated_at)}</td>
                  <td>{n.created_by_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
